import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DynamicBoardCard, BoardField, BoardCardChat } from '../board-v3.types';

@Component({
  selector: 'app-board-v3-column-body',
  templateUrl: './column-body.component.html',
  styleUrls: ['./column-body.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex h-full min-h-0 flex-1 flex-col overflow-hidden' },
})
export class ColumnBodyComponent {
  @ViewChild(CdkVirtualScrollViewport) private viewport!: CdkVirtualScrollViewport;

  @Input({ required: true }) columnId!: string;
  @Input() cards: DynamicBoardCard[] = [];
  @Input() connectedTo: string[] = [];
  @Input() itemSize: number = 180;
  @Input() viewFields: string[] = [];
  @Input() allFields: BoardField[] = [];
  @Input() canMoveCards: boolean = false;
  @Input() hasMore: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() quickCreateTemplate: TemplateRef<unknown> | null = null;
  @Input() selectionMode: boolean = false;
  @Input() selectedCardIds: ReadonlySet<string> = new Set<string>();

  @Output() cardSelectionToggle = new EventEmitter<DynamicBoardCard>();
  @Output() cardDrop = new EventEmitter<CdkDragDrop<DynamicBoardCard[]>>();
  @Output() cardClick = new EventEmitter<DynamicBoardCard>();
  @Output() cardDelete = new EventEmitter<DynamicBoardCard>();
  @Output() cardMoveClick = new EventEmitter<DynamicBoardCard>();
  @Output() chatClick = new EventEmitter<BoardCardChat>();
  @Output() loadMore = new EventEmitter<void>();
  @Output() cardDragStart = new EventEmitter<void>();
  @Output() cardDragEnd = new EventEmitter<void>();

  protected readonly dragStartDelay = { touch: 300, mouse: 0 };

  trackById = (_: number, card: DynamicBoardCard): string => card.id;

  isSelected(card: DynamicBoardCard): boolean {
    return this.selectedCardIds.has(card.id);
  }

  onDrop(event: CdkDragDrop<DynamicBoardCard[]>): void {
    const draggedCard = event.item.data as DynamicBoardCard;
    const realPrevIndex = event.previousContainer.data.findIndex((c) => c.id === draggedCard.id);
    const scrollOffset = this.viewport ? this.viewport.getRenderedRange().start : 0;
    const realCurrentIndex = event.currentIndex + scrollOffset;
    this.cardDrop.emit({ ...event, previousIndex: realPrevIndex, currentIndex: realCurrentIndex });
  }

  onScrolledIndexChange(firstIndex: number): void {
    if (!this.hasMore || this.isLoading) return;
    if (firstIndex >= this.cards.length - 5) {
      this.loadMore.emit();
    }
  }

  onScroll(event: Event): void {
    if (!this.hasMore || this.isLoading) return;

    const element = event.target as HTMLElement;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 120) {
      this.loadMore.emit();
    }
  }
}
